/**
 * Client HTTP du backend souverain Klavox (`apps/server`, voir son README
 * pour le contrat exact). Ce module ne fait QUE mettre en forme les requetes
 * et reponses -- il ne derive ni ne dechiffre jamais rien : tout ce qu'il
 * envoie (`masterPasswordHashBase64`, `protectedSymmetricKey`, entrees/
 * dossiers chiffres) est deja produit par `@klavox/crypto-core` /
 * `@klavox/vault-model` avant d'arriver ici.
 */
import type { KdfParams } from '@klavox/crypto-core';
import { createSessionRefresher } from '@santvia/ui-socle/session';
import { parseSecurityLog, type SecurityLogEvent } from './security-log.js';
import { parseConnectedDevices, type ConnectedDevice } from './connected-devices.js';
import type {
  EncryptedVaultAttachment,
  EncryptedVaultFolder,
  EncryptedVaultItem,
  VaultAttachmentMetadata,
} from '@klavox/vault-model';
import type {
  AuthenticationResponseJSON,
  CreationOptionsJSON,
  RegistrationResponseJSON,
  RequestOptionsJSON,
} from './webauthn-browser.js';

export type KlavoxApiErrorCode =
  | 'invalid_request'
  | 'account_already_exists'
  | 'authentication_failed'
  | 'session_expired'
  | 'recovery_unavailable'
  | 'mfa_challenge_expired'
  | 'webauthn_failed'
  | 'quota_exceeded'
  | 'not_found'
  | 'oidc_identity_taken'
  | 'totp_invalid'
  | 'network_error'
  | 'unexpected_response';

export class KlavoxApiError extends Error {
  readonly code: KlavoxApiErrorCode;
  readonly status: number;

  constructor(code: KlavoxApiErrorCode, status: number, message: string) {
    super(message);
    this.name = 'KlavoxApiError';
    this.code = code;
    this.status = status;
  }
}

export interface RegisteredAccount {
  readonly accountId: string;
  readonly email: string;
}

export interface AuthenticatedSession {
  readonly sessionToken: string;
  readonly refreshToken: string;
  readonly expiresAt: number;
  readonly kdfParams: KdfParams;
  readonly protectedSymmetricKey: string;
}

/** Reponse de connexion quand le compte exige un second facteur : aucune session n'est encore emise. */
export interface MfaRequired {
  readonly mfaRequired: true;
  readonly mfaToken: string;
  /** Facteurs actifs sur le compte ; absent d'un serveur anterieur au TOTP de compte, qui ne connait que WebAuthn. */
  readonly methods?: readonly MfaMethod[];
}

export type MfaMethod = 'webauthn' | 'totp';

/** Facteurs a proposer pour une connexion en attente : lus dans la reponse, jamais devines au-dela de WebAuthn. */
export function mfaMethods(result: MfaRequired): readonly MfaMethod[] {
  const methods = Array.isArray(result.methods) ? result.methods.filter((m) => m === 'webauthn' || m === 'totp') : [];
  return methods.length > 0 ? methods : ['webauthn'];
}

/**
 * Code tape par l'utilisateur, tel que le serveur l'attend : les espaces et tirets que les applications affichent
 * (« 123 456 ») sont retires ; tout autre ecart rend `null`, refuse avant le reseau.
 */
export function normalizeTotpCode(raw: string): string | null {
  const code = raw.replace(/[\s-]/gu, '');
  return /^\d{6}$/u.test(code) ? code : null;
}

/** Graine proposee a l'activation du second facteur TOTP : affichee une fois, jamais stockee par le client. */
export interface TotpEnrollment {
  readonly secret: string;
  readonly otpauthUri: string;
}

/** Authentificateur enregistre sur le compte -- donnees publiques uniquement. */
export interface WebAuthnCredentialSummary {
  readonly credentialId: string;
  readonly label?: string;
  readonly createdAt: string;
  readonly deviceType: 'singleDevice' | 'multiDevice';
  readonly backedUp: boolean;
}

export function isMfaRequired(result: AuthenticatedSession | MfaRequired): result is MfaRequired {
  return (result as MfaRequired).mfaRequired === true;
}

export interface SyncPushResult {
  readonly items: readonly EncryptedVaultItem[];
  readonly folders: readonly EncryptedVaultFolder[];
  /** Metadonnees seules (absent sur un serveur anterieur aux pieces jointes). */
  readonly attachments?: readonly VaultAttachmentMetadata[];
  readonly conflicts: number;
  readonly serverTime: number;
}

export interface SyncPullResult {
  readonly items: readonly EncryptedVaultItem[];
  readonly folders: readonly EncryptedVaultFolder[];
  /** Metadonnees seules, contenu via `fetchAttachment` (absent sur un serveur anterieur aux pieces jointes). */
  readonly attachments?: readonly VaultAttachmentMetadata[];
  readonly serverTime: number;
}

function joinUrl(baseUrl: string, path: string): string {
  return `${baseUrl.replace(/\/+$/u, '')}${path}`;
}

async function request(baseUrl: string, path: string, init: RequestInit): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(joinUrl(baseUrl, path), {
      ...init,
      headers: { 'content-type': 'application/json', ...init.headers },
    });
  } catch (cause) {
    const message = cause instanceof Error ? cause.message : 'requete reseau echouee';
    throw new KlavoxApiError('network_error', 0, message);
  }

  if (response.status === 204) return undefined;

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    if (response.ok) return undefined;
    throw new KlavoxApiError('unexpected_response', response.status, 'reponse serveur illisible');
  }

  if (!response.ok) {
    const candidate = body as { error?: unknown; message?: unknown };
    const code: KlavoxApiErrorCode =
      typeof candidate.error === 'string' &&
      [
        'invalid_request',
        'account_already_exists',
        'authentication_failed',
        'session_expired',
        'recovery_unavailable',
        'mfa_challenge_expired',
        'webauthn_failed',
        'quota_exceeded',
        'not_found',
        'oidc_identity_taken',
        'totp_invalid',
      ].includes(candidate.error)
        ? (candidate.error as KlavoxApiErrorCode)
        : 'unexpected_response';
    const message = typeof candidate.message === 'string' ? candidate.message : 'erreur serveur';
    throw new KlavoxApiError(code, response.status, message);
  }
  return body;
}

function authHeader(sessionToken: string): Record<string, string> {
  return { authorization: `Bearer ${sessionToken}` };
}

export async function registerAccount(
  baseUrl: string,
  input: {
    readonly email: string;
    readonly kdfParams: KdfParams;
    readonly masterPasswordHashBase64: string;
    readonly protectedSymmetricKey: string;
  },
): Promise<RegisteredAccount> {
  return (await request(baseUrl, '/v1/accounts', {
    method: 'POST',
    body: JSON.stringify(input),
  })) as RegisteredAccount;
}

export async function login(
  baseUrl: string,
  input: { readonly email: string; readonly masterPasswordHashBase64: string },
): Promise<AuthenticatedSession | MfaRequired> {
  return (await request(baseUrl, '/v1/sessions', {
    method: 'POST',
    body: JSON.stringify(input),
  })) as AuthenticatedSession | MfaRequired;
}

export async function beginWebAuthnLogin(baseUrl: string, mfaToken: string): Promise<RequestOptionsJSON> {
  return (await request(baseUrl, '/v1/sessions/webauthn/begin', {
    method: 'POST',
    body: JSON.stringify({ mfaToken }),
  })) as RequestOptionsJSON;
}

export async function finishWebAuthnLogin(
  baseUrl: string,
  mfaToken: string,
  response: AuthenticationResponseJSON,
): Promise<AuthenticatedSession> {
  return (await request(baseUrl, '/v1/sessions/webauthn/finish', {
    method: 'POST',
    body: JSON.stringify({ mfaToken, response }),
  })) as AuthenticatedSession;
}

export async function finishTotpLogin(baseUrl: string, mfaToken: string, code: string): Promise<AuthenticatedSession> {
  return (await request(baseUrl, '/v1/sessions/totp/finish', {
    method: 'POST',
    body: JSON.stringify({ mfaToken, code }),
  })) as AuthenticatedSession;
}

export async function getTotpStatus(baseUrl: string, sessionToken: string): Promise<boolean> {
  const body = (await request(baseUrl, '/v1/accounts/totp', { method: 'GET', headers: authHeader(sessionToken) })) as {
    enabled?: unknown;
  };
  if (typeof body?.enabled !== 'boolean') throw new KlavoxApiError('unexpected_response', 200, 'reponse serveur illisible');
  return body.enabled;
}

export async function beginTotpEnrollment(baseUrl: string, sessionToken: string): Promise<TotpEnrollment> {
  const body = (await request(baseUrl, '/v1/accounts/totp/begin', { method: 'POST', headers: authHeader(sessionToken) })) as {
    secret?: unknown;
    otpauthUri?: unknown;
  };
  // Revalidee : la graine part dans un code QR et sous les yeux de l'utilisateur, jamais une valeur d'une autre forme.
  if (
    typeof body?.secret !== 'string' ||
    !/^[A-Z2-7]{16,64}$/u.test(body.secret) ||
    typeof body.otpauthUri !== 'string' ||
    !body.otpauthUri.startsWith('otpauth://totp/') ||
    !body.otpauthUri.includes(`secret=${body.secret}&`)
  ) {
    throw new KlavoxApiError('unexpected_response', 200, 'reponse serveur illisible');
  }
  return { secret: body.secret, otpauthUri: body.otpauthUri };
}

/** Activation : un code de l'application ET la preuve du mot de passe maitre, comme toute pose de facteur. */
export async function finishTotpEnrollment(
  baseUrl: string,
  sessionToken: string,
  input: { readonly code: string; readonly masterPasswordHashBase64: string },
): Promise<void> {
  await request(baseUrl, '/v1/accounts/totp/finish', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify(input),
  });
}

export async function disableTotp(baseUrl: string, sessionToken: string, masterPasswordHashBase64: string): Promise<void> {
  await request(baseUrl, '/v1/accounts/totp', {
    method: 'DELETE',
    headers: authHeader(sessionToken),
    body: JSON.stringify({ masterPasswordHashBase64 }),
  });
}

export async function beginWebAuthnRegistration(baseUrl: string, sessionToken: string): Promise<CreationOptionsJSON> {
  return (await request(baseUrl, '/v1/accounts/webauthn/register/begin', {
    method: 'POST',
    headers: authHeader(sessionToken),
  })) as CreationOptionsJSON;
}

export async function finishWebAuthnRegistration(
  baseUrl: string,
  sessionToken: string,
  input: { readonly response: RegistrationResponseJSON; readonly label?: string },
): Promise<WebAuthnCredentialSummary> {
  return (await request(baseUrl, '/v1/accounts/webauthn/register/finish', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify(input),
  })) as WebAuthnCredentialSummary;
}

export async function listWebAuthnCredentials(baseUrl: string, sessionToken: string): Promise<WebAuthnCredentialSummary[]> {
  const body = (await request(baseUrl, '/v1/accounts/webauthn/credentials', {
    method: 'GET',
    headers: authHeader(sessionToken),
  })) as { credentials?: unknown };
  if (!Array.isArray(body?.credentials)) throw new KlavoxApiError('unexpected_response', 200, 'reponse serveur illisible');
  return body.credentials as WebAuthnCredentialSummary[];
}

/** Revocation : le serveur exige la preuve du mot de passe maitre, pas seulement la session. */
export async function deleteWebAuthnCredential(
  baseUrl: string,
  sessionToken: string,
  credentialId: string,
  masterPasswordHashBase64: string,
): Promise<void> {
  await request(baseUrl, `/v1/accounts/webauthn/credentials/${encodeURIComponent(credentialId)}`, {
    method: 'DELETE',
    headers: authHeader(sessionToken),
    body: JSON.stringify({ masterPasswordHashBase64 }),
  });
}

/**
 * Change le mot de passe maitre du compte serveur. Le serveur ne recoit que
 * des preuves et des blobs deja produits localement (jamais un mot de passe) ;
 * il revoque toutes les sessions et en emet une nouvelle, renvoyee ici.
 */
export async function changeServerMasterPassword(
  baseUrl: string,
  sessionToken: string,
  input: {
    readonly currentMasterPasswordHashBase64: string;
    readonly newMasterPasswordHashBase64: string;
    readonly newProtectedSymmetricKey: string;
    readonly newKdfParams: KdfParams;
  },
): Promise<AuthenticatedSession> {
  return (await request(baseUrl, '/v1/accounts/master-password', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify(input),
  })) as AuthenticatedSession;
}

/**
 * Change l'adresse e-mail du compte serveur (`POST /v1/accounts/email`). Comme pour le mot de passe maitre, le
 * serveur ne recoit que des preuves et l'enveloppe re-scellee sous le nouvel e-mail (le sel) ; il revoque toutes
 * les sessions et en emet une nouvelle, que l'appelant substitue a l'ancienne.
 */
export async function changeServerEmail(
  baseUrl: string,
  sessionToken: string,
  input: {
    readonly newEmail: string;
    readonly currentMasterPasswordHashBase64: string;
    readonly newMasterPasswordHashBase64: string;
    readonly newProtectedSymmetricKey: string;
    readonly newKdfParams: KdfParams;
  },
): Promise<AuthenticatedSession> {
  return (await request(baseUrl, '/v1/accounts/email', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify(input),
  })) as AuthenticatedSession;
}

export async function refreshSession(baseUrl: string, refreshToken: string): Promise<AuthenticatedSession> {
  return (await request(baseUrl, '/v1/sessions/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  })) as AuthenticatedSession;
}

/** Ce qu'il faut d'une session serveur pour la renouveler ; le reste (`lastSyncedAt`...) traverse intact. */
export interface RenewableServerSession {
  readonly baseUrl: string;
  readonly sessionToken: string;
  readonly refreshToken: string;
  readonly expiresAt: number;
}

/**
 * Renouvelle la session serveur par l'entree PARTAGEE du socle (`createSessionRefresher`, `@santvia/ui-socle/session`),
 * jamais par une logique propre a Klavox. Le jeton de renouvellement est TOURNANT et a usage unique
 * (`apps/server/src/session-store.ts`) : deux demandes qui le presentent en meme temps -- deux gestes du meme onglet,
 * une synchro et un chargement de reglages -- font echouer la seconde, qui repart avec un jeton deja consomme.
 * Le socle repart de la session RANGEE (`load`), rend une session encore fraiche sans renouveler, et PARTAGE un
 * renouvellement en cours : deux appels simultanes n'en declenchent qu'un.
 * La session neuve n'est rangee que si la session lue est TOUJOURS celle en place : verrouille, deconnecte ou
 * reconnecte pendant l'attente, rien n'est ressuscite.
 */
export function createServerSessionRenewer<S extends RenewableServerSession>(deps: {
  readonly load: () => S | undefined;
  readonly save: (session: S) => void;
  readonly now?: () => number;
  readonly marginMs?: number;
}): () => Promise<S> {
  let loaded: S | undefined;
  const renew = createSessionRefresher<S, undefined>({
    load: async () => (loaded = deps.load()),
    save: async (session) => {
      if (deps.load() === loaded) deps.save(session);
    },
    refresh: async (_config, stored) => {
      const renewed = await refreshSession(stored.baseUrl, stored.refreshToken);
      return { ...stored, sessionToken: renewed.sessionToken, refreshToken: renewed.refreshToken, expiresAt: renewed.expiresAt };
    },
    ...(deps.now === undefined ? {} : { now: deps.now }),
    ...(deps.marginMs === undefined ? {} : { marginMs: deps.marginMs }),
  });
  return () => renew(undefined);
}

export async function logout(baseUrl: string, sessionToken: string): Promise<void> {
  await request(baseUrl, '/v1/sessions/logout', {
    method: 'POST',
    headers: authHeader(sessionToken),
  });
}

/**
 * Emetteur et `client_id` Idenva de ce serveur (`GET /v1/config/idenva`, non
 * authentifie), a passer a `beginIdenvaLink`. `null` si l'instance n'a pas
 * Idenva (404) : la PWA n'offre alors pas la liaison. Une reponse qui n'a pas
 * cette forme exacte est refusee plutot que de lancer une liaison vers un
 * emetteur mal lu.
 */
export async function fetchIdenvaConfig(
  baseUrl: string,
): Promise<{ readonly issuer: string; readonly clientId: string } | null> {
  let body: unknown;
  try {
    body = await request(baseUrl, '/v1/config/idenva', { method: 'GET' });
  } catch (cause) {
    if (cause instanceof KlavoxApiError && cause.code === 'not_found') return null;
    throw cause;
  }
  const candidate = body as { issuer?: unknown; clientId?: unknown } | undefined;
  if (
    typeof candidate?.issuer !== 'string' ||
    candidate.issuer.length === 0 ||
    typeof candidate.clientId !== 'string' ||
    candidate.clientId.length === 0
  ) {
    throw new KlavoxApiError('unexpected_response', 200, 'configuration Idenva illisible');
  }
  return { issuer: candidate.issuer, clientId: candidate.clientId };
}

/**
 * Lie l'identite Idenva d'un ID token (`idenva-link.ts`) au compte. Le serveur
 * verifie le jeton et exige la preuve du mot de passe maitre, pas seulement la
 * session : une session volee ne branche pas l'Idenva d'un tiers sur le coffre.
 */
export async function linkOidcIdentity(
  baseUrl: string,
  sessionToken: string,
  input: { readonly idToken: string; readonly nonce: string; readonly masterPasswordHashBase64: string },
): Promise<void> {
  await request(baseUrl, '/v1/accounts/oidc/link', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify(input),
  });
}

/** Reponse de `lookupOidcAccount` : `email` = `null` pour un coffre cree depuis Idenva (sel aleatoire). */
export type OidcAccountLookup =
  | { readonly exists: false }
  | { readonly exists: true; readonly kdfParams: KdfParams; readonly email: string | null };

/**
 * Un coffre existe-t-il pour l'identite de cet ID token (`POST /v1/accounts/oidc/lookup`) ? Le serveur verifie le
 * jeton ; la PWA choisit ainsi elle-meme entre « Ouvrir votre coffre » et « Creer votre coffre ».
 */
export async function lookupOidcAccount(baseUrl: string, token: { readonly idToken: string; readonly nonce: string }): Promise<OidcAccountLookup> {
  const body = (await request(baseUrl, '/v1/accounts/oidc/lookup', {
    method: 'POST',
    body: JSON.stringify({ idToken: token.idToken, nonce: token.nonce }),
  })) as { exists?: unknown; kdfParams?: unknown; email?: unknown } | undefined;
  if (body?.exists === false) return { exists: false };
  if (body?.exists !== true || typeof body.kdfParams !== 'object' || body.kdfParams === null || (body.email !== null && typeof body.email !== 'string')) {
    throw new KlavoxApiError('unexpected_response', 200, 'reponse Idenva illisible');
  }
  return { exists: true, kdfParams: body.kdfParams as KdfParams, email: body.email };
}

/**
 * Cree le compte serveur SANS e-mail, rattache au `sub` de l'ID token (`POST /v1/accounts/oidc`), et rend sa
 * session. `kdfParams` porte le sel aleatoire du coffre : sans e-mail, le serveur refuse tout autre sel.
 */
export async function registerWithIdenva(
  baseUrl: string,
  input: {
    readonly idToken: string;
    readonly nonce: string;
    readonly kdfParams: KdfParams;
    readonly masterPasswordHashBase64: string;
    readonly protectedSymmetricKey: string;
  },
): Promise<AuthenticatedSession & { readonly accountId: string }> {
  return (await request(baseUrl, '/v1/accounts/oidc', {
    method: 'POST',
    body: JSON.stringify(input),
  })) as AuthenticatedSession & { readonly accountId: string };
}

/** Connexion par Idenva (`POST /v1/sessions/oidc`) : le jeton designe le compte, la preuve du mot de passe maitre l'ouvre. */
export async function loginWithIdenva(
  baseUrl: string,
  input: { readonly idToken: string; readonly nonce: string; readonly masterPasswordHashBase64: string },
): Promise<AuthenticatedSession | MfaRequired> {
  return (await request(baseUrl, '/v1/sessions/oidc', {
    method: 'POST',
    body: JSON.stringify(input),
  })) as AuthenticatedSession | MfaRequired;
}

/**
 * Le compte est-il lie a Idenva (`GET /v1/accounts/oidc/link`) ? Le serveur ne
 * rend qu'un booleen, jamais le `sub`. Une reponse sans booleen est refusee :
 * un etat mal lu ne doit pas faire disparaitre le bouton « Delier ».
 */
export async function fetchOidcLinkStatus(baseUrl: string, sessionToken: string): Promise<boolean> {
  const body = (await request(baseUrl, '/v1/accounts/oidc/link', {
    method: 'GET',
    headers: authHeader(sessionToken),
  })) as { linked?: unknown } | undefined;
  if (typeof body?.linked !== 'boolean') {
    throw new KlavoxApiError('unexpected_response', 200, 'etat de liaison Idenva illisible');
  }
  return body.linked;
}

/** Retire la liaison Idenva du compte -- meme exigence que la liaison. */
export async function unlinkOidcIdentity(
  baseUrl: string,
  sessionToken: string,
  masterPasswordHashBase64: string,
): Promise<void> {
  await request(baseUrl, '/v1/accounts/oidc/link', {
    method: 'DELETE',
    headers: authHeader(sessionToken),
    body: JSON.stringify({ masterPasswordHashBase64 }),
  });
}

/**
 * Mode voyage du compte (R18). L'etat est porte par le COMPTE, pas par
 * l'appareil : c'est ce qui permet de desactiver depuis un autre appareil.
 */
/**
 * Supprime le compte serveur et tout ce que le serveur en garde (droit a l'effacement,
 * `docs/DATA_PROTECTION.md` §3). Le coffre LOCAL n'est pas touche : c'est a l'appelant d'en decider.
 */
export async function deleteServerAccount(baseUrl: string, sessionToken: string, masterPasswordHashBase64: string): Promise<void> {
  await request(baseUrl, '/v1/accounts', {
    method: 'DELETE',
    headers: authHeader(sessionToken),
    body: JSON.stringify({ masterPasswordHashBase64 }),
  });
}

/**
 * Deconnecte TOUS les appareils du compte (`POST /v1/sessions/revoke-all`), celui-ci compris : le serveur ne
 * recoit que la preuve du mot de passe maitre, revoque chaque session et chaque jeton de renouvellement, puis
 * rend une session neuve, que l'appelant doit substituer a l'ancienne (devenue invalide).
 */
export async function revokeAllServerSessions(baseUrl: string, sessionToken: string, masterPasswordHashBase64: string): Promise<AuthenticatedSession> {
  return (await request(baseUrl, '/v1/sessions/revoke-all', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify({ masterPasswordHashBase64 }),
  })) as AuthenticatedSession;
}

/**
 * Appareils connectes au compte (`GET /v1/sessions`) : la session suffit. Chaque ligne est revalidee par
 * `parseConnectedDevices` -- le navigateur vient de QUI s'est connecte.
 */
export async function fetchConnectedDevices(baseUrl: string, sessionToken: string): Promise<ConnectedDevice[]> {
  const body = await request(baseUrl, '/v1/sessions', {
    method: 'GET',
    headers: authHeader(sessionToken),
  });
  try {
    return parseConnectedDevices(body);
  } catch {
    throw new KlavoxApiError('unexpected_response', 200, 'reponse serveur illisible');
  }
}

/** Deconnecte UN appareil du compte (`DELETE /v1/sessions/:id`) : sessions et renouvellement ; rien d'autre ne part. */
export async function revokeConnectedDevice(baseUrl: string, sessionToken: string, deviceId: string): Promise<void> {
  await request(baseUrl, `/v1/sessions/${encodeURIComponent(deviceId)}`, {
    method: 'DELETE',
    headers: authHeader(sessionToken),
  });
}

/**
 * Journal de securite du compte (`GET /v1/accounts/security-events`) : la session suffit, rien ne part
 * que le jeton. Chaque ligne est revalidee par `parseSecurityLog` -- IP et navigateur viennent de QUI a
 * frappe a la porte, un tiers compris.
 */
export async function fetchSecurityLog(baseUrl: string, sessionToken: string): Promise<SecurityLogEvent[]> {
  const body = await request(baseUrl, '/v1/accounts/security-events', {
    method: 'GET',
    headers: authHeader(sessionToken),
  });
  try {
    return parseSecurityLog(body);
  } catch {
    throw new KlavoxApiError('unexpected_response', 200, 'reponse serveur illisible');
  }
}

export async function fetchTravelMode(baseUrl: string, sessionToken: string): Promise<{ readonly enabled: boolean }> {
  return (await request(baseUrl, '/v1/accounts/travel-mode', {
    method: 'GET',
    headers: authHeader(sessionToken),
  })) as { enabled: boolean };
}

/**
 * Arme ou desarme le mode voyage. Le serveur REFUSE le desarmement demande
 * par l'appareil qui a arme : il faut un autre appareil.
 */
export async function setTravelMode(
  baseUrl: string,
  sessionToken: string,
  input: { readonly enabled: boolean; readonly deviceId: string },
): Promise<{ readonly enabled: boolean }> {
  return (await request(baseUrl, '/v1/accounts/travel-mode', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify(input),
  })) as { enabled: boolean };
}

/** Etat d'une delegation d'acces d'urgence vue par son TITULAIRE (R32). */
export interface EmergencyGrantSummary {
  readonly id: string;
  /** Libelle du contact, scelle sous la `vaultKey` : a ouvrir localement. */
  readonly contactLabel: string;
  readonly waitMs: number;
  readonly createdAt: number;
  readonly status: 'idle' | 'waiting' | 'granted' | 'revoked';
  readonly requestedAt: number | null;
  readonly availableAt: number | null;
  readonly lastRefusedAt: number | null;
  readonly revokedAt: number | null;
}

/** Ce que le CONTACT apprend en presentant sa preuve : rien sur le compte vise. */
export interface EmergencyRequestState {
  readonly grantId: string;
  readonly status: 'idle' | 'waiting' | 'granted' | 'revoked';
  readonly waitMs: number;
  readonly availableAt: number | null;
}

/** Ce que le contact retire une fois le delai ecoule. Aucune session : lecture seule. */
export interface EmergencyReleaseResult {
  readonly grantId: string;
  readonly protectedKey: string;
  readonly items: readonly EncryptedVaultItem[];
  readonly folders: readonly EncryptedVaultFolder[];
  readonly attachments: readonly VaultAttachmentMetadata[];
  readonly serverTime: number;
}

/**
 * Depose une delegation d'acces d'urgence. Le serveur ne recoit QUE des blobs
 * deja scelles localement : ni le kit remis au contact, ni la preuve qui en
 * derive, ni la `vaultKey`.
 */
export async function createEmergencyGrant(
  baseUrl: string,
  sessionToken: string,
  input: {
    readonly id: string;
    readonly verifier: string;
    readonly protectedKey: string;
    readonly contactLabel: string;
    readonly waitMs: number;
  },
): Promise<EmergencyGrantSummary> {
  return (await request(baseUrl, '/v1/accounts/emergency-access', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify(input),
  })) as EmergencyGrantSummary;
}

export async function listEmergencyGrants(baseUrl: string, sessionToken: string): Promise<EmergencyGrantSummary[]> {
  const body = (await request(baseUrl, '/v1/accounts/emergency-access', {
    method: 'GET',
    headers: authHeader(sessionToken),
  })) as { grants?: unknown };
  if (!Array.isArray(body?.grants)) throw new KlavoxApiError('unexpected_response', 200, 'reponse serveur illisible');
  return body.grants as EmergencyGrantSummary[];
}

/** Le titulaire REFUSE la demande en cours : l'attente est annulee, la delegation reste. */
export async function refuseEmergencyGrant(
  baseUrl: string,
  sessionToken: string,
  id: string,
): Promise<EmergencyGrantSummary> {
  return (await request(baseUrl, `/v1/accounts/emergency-access/${encodeURIComponent(id)}/refuse`, {
    method: 'POST',
    headers: authHeader(sessionToken),
  })) as EmergencyGrantSummary;
}

/** Le titulaire REVOQUE : definitif, le serveur efface le blob scelle. */
export async function revokeEmergencyGrant(baseUrl: string, sessionToken: string, id: string): Promise<void> {
  await request(baseUrl, `/v1/accounts/emergency-access/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeader(sessionToken),
  });
}

/**
 * Voie du CONTACT : aucune session, sa preuve suffit. La preuve est derivee du
 * kit remis hors ligne (`deriveEmergencyAccessProof`) -- le kit lui-meme ne
 * quitte JAMAIS cet appareil.
 */
export async function requestEmergencyAccess(baseUrl: string, accessProof: string): Promise<EmergencyRequestState> {
  return (await request(baseUrl, '/v1/emergency-access/request', {
    method: 'POST',
    body: JSON.stringify({ accessProof }),
  })) as EmergencyRequestState;
}

export async function releaseEmergencyAccess(baseUrl: string, accessProof: string): Promise<EmergencyReleaseResult> {
  return (await request(baseUrl, '/v1/emergency-access/release', {
    method: 'POST',
    body: JSON.stringify({ accessProof }),
  })) as EmergencyReleaseResult;
}

export async function pushSync(
  baseUrl: string,
  sessionToken: string,
  batch: {
    readonly items?: readonly EncryptedVaultItem[];
    readonly folders?: readonly EncryptedVaultFolder[];
    readonly attachments?: readonly EncryptedVaultAttachment[];
  },
): Promise<SyncPushResult> {
  return (await request(baseUrl, '/v1/vault/sync', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify(batch),
  })) as SyncPushResult;
}

export async function pullSync(baseUrl: string, sessionToken: string, since = 0): Promise<SyncPullResult> {
  return (await request(baseUrl, `/v1/vault/sync?since=${since}`, {
    method: 'GET',
    headers: authHeader(sessionToken),
  })) as SyncPullResult;
}

/** Piece jointe chiffree complete. Non authentifiee : a passer par `authenticateVaultRecords` avant tout usage. */
export async function fetchAttachment(baseUrl: string, sessionToken: string, id: string): Promise<EncryptedVaultAttachment> {
  return (await request(baseUrl, `/v1/vault/attachments/${encodeURIComponent(id)}`, {
    method: 'GET',
    headers: authHeader(sessionToken),
  })) as EncryptedVaultAttachment;
}

export interface RecoveryInfo {
  readonly kdfParams: KdfParams;
  readonly recoveryProtectedSymmetricKey: string;
}

/**
 * Publie la `vaultKey` scellee sous la cle de recuperation sur le serveur souverain, pour ce compte, avec la
 * preuve de possession de la cle (`recoveryAuthProof`) qu'il exigera pour reinitialiser.
 */
export async function enableServerRecoveryKey(
  baseUrl: string,
  sessionToken: string,
  recoveryProtectedSymmetricKey: string,
  recoveryAuthProofBase64: string,
): Promise<void> {
  await request(baseUrl, '/v1/accounts/recovery-key', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify({ recoveryProtectedSymmetricKey, recoveryAuthProofBase64 }),
  });
}

/**
 * Etat de la cle de recuperation du compte : `enabled` = une enveloppe est deposee, `proof` = son verificateur
 * aussi. Une cle activee avant la preuve de possession (`enabled && !proof`) est refusee a la reinitialisation.
 */
export async function getRecoveryKeyStatus(
  baseUrl: string,
  sessionToken: string,
): Promise<{ readonly enabled: boolean; readonly proof: boolean }> {
  const body = (await request(baseUrl, '/v1/accounts/recovery-key/status', { method: 'GET', headers: authHeader(sessionToken) })) as {
    enabled?: unknown;
    proof?: unknown;
  };
  if (typeof body?.enabled !== 'boolean' || typeof body.proof !== 'boolean') {
    throw new KlavoxApiError('unexpected_response', 200, 'reponse serveur illisible');
  }
  return { enabled: body.enabled, proof: body.proof };
}

/** Recupere de quoi tenter une reinitialisation locale depuis un autre appareil (aucune session requise). */
export async function fetchServerRecoveryInfo(baseUrl: string, email: string): Promise<RecoveryInfo> {
  return (await request(baseUrl, `/v1/accounts/recovery-key?email=${encodeURIComponent(email)}`, {
    method: 'GET',
  })) as RecoveryInfo;
}

/** Parametres KDF du compte, a appliquer avant de deriver sur un appareil vierge (aucune session requise). */
export async function fetchServerPrelogin(baseUrl: string, email: string): Promise<{ readonly kdfParams: KdfParams }> {
  return (await request(baseUrl, `/v1/accounts/prelogin?email=${encodeURIComponent(email)}`, {
    method: 'GET',
  })) as { readonly kdfParams: KdfParams };
}

/** Soumet les nouveaux identifiants produits localement via la cle de recuperation ; reconnecte l'appareil. */
export async function resetAccountWithRecoveryKey(
  baseUrl: string,
  input: {
    readonly email: string;
    /** Preuve de possession de la cle de recuperation : sans elle, le serveur refuse. */
    readonly recoveryAuthProofBase64: string;
    readonly newMasterPasswordHashBase64: string;
    readonly newProtectedSymmetricKey: string;
    readonly newKdfParams: KdfParams;
  },
): Promise<AuthenticatedSession> {
  return (await request(baseUrl, '/v1/accounts/recovery', {
    method: 'POST',
    body: JSON.stringify(input),
  })) as AuthenticatedSession;
}

// --- Klavox Send (R15) --------------------------------------------------
// Transport seulement : la cle d'un envoi ne passe JAMAIS par ici (elle vit
// dans le fragment `#` du lien de partage, voir `send-client.ts`).

/** Ce que l'emetteur depose. `ciphertext` et `secrets` sont deja scelles, sous deux cles differentes. */
export interface SendCreationRequest {
  readonly id: string;
  readonly ciphertext: string;
  readonly secrets: string;
  readonly saltBase64: string;
  readonly kdfParams: KdfParams | null;
  readonly expiresAt: number;
  readonly maxViews: number | null;
}

/** Envoi tel que son emetteur le voit ; `secrets` ne s'ouvre que sous sa `vaultKey`. */
export interface OwnedSendSummary {
  readonly id: string;
  readonly secrets: string;
  readonly createdAt: number;
  readonly expiresAt: number;
  readonly maxViews: number | null;
  readonly views: number;
  readonly revokedAt: number | null;
  readonly passwordProtected: boolean;
}

/** Etat public d'un envoi, lisible sans compte et SANS consommer de vue. */
export interface SendDescription {
  readonly id: string;
  readonly passwordProtected: boolean;
  readonly expiresAt: number;
  readonly maxViews: number | null;
  readonly views: number;
}

export interface SendContent extends SendDescription {
  readonly ciphertext: string;
  readonly saltBase64: string;
  readonly kdfParams: KdfParams | null;
}

export async function createSend(
  baseUrl: string,
  sessionToken: string,
  input: SendCreationRequest,
): Promise<{ readonly id: string; readonly expiresAt: number }> {
  return (await request(baseUrl, '/v1/sends', {
    method: 'POST',
    headers: authHeader(sessionToken),
    body: JSON.stringify(input),
  })) as { id: string; expiresAt: number };
}

export async function listSends(baseUrl: string, sessionToken: string): Promise<readonly OwnedSendSummary[]> {
  const body = (await request(baseUrl, '/v1/sends', {
    method: 'GET',
    headers: authHeader(sessionToken),
  })) as { sends?: readonly OwnedSendSummary[] };
  return body.sends ?? [];
}

/** Ce qu'un emetteur peut changer d'un envoi depose : echeance, plafond, et son propre blob scelle. */
export interface SendUpdateRequest {
  readonly expiresAt: number;
  readonly maxViews: number | null;
  readonly secrets: string;
}

export async function updateSend(
  baseUrl: string,
  sessionToken: string,
  id: string,
  input: SendUpdateRequest,
): Promise<{ readonly id: string; readonly expiresAt: number }> {
  return (await request(baseUrl, `/v1/sends/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: authHeader(sessionToken),
    body: JSON.stringify(input),
  })) as { id: string; expiresAt: number };
}

export async function revokeSend(baseUrl: string, sessionToken: string, id: string): Promise<void> {
  await request(baseUrl, `/v1/sends/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: authHeader(sessionToken),
  });
}

/** Metadonnees d'un envoi, sans consommer de vue -- permet de demander le mot de passe avant. */
export async function describeSend(baseUrl: string, id: string): Promise<SendDescription> {
  return (await request(baseUrl, `/v1/sends/${encodeURIComponent(id)}`, { method: 'GET' })) as SendDescription;
}

/** Consomme une vue et telecharge le ciphertext. A ne faire qu'UNE fois : reessayer un mot de passe se fait hors ligne. */
export async function accessSend(baseUrl: string, id: string): Promise<SendContent> {
  return (await request(baseUrl, `/v1/sends/${encodeURIComponent(id)}/access`, { method: 'POST' })) as SendContent;
}
